import { createSignal, createUniqueId, For, Show } from "solid-js";
import type { FormData, ReferralQuestion } from "./types";
import { sendVerificationCode, verifyPhone } from "./api";
import { StepActions } from "./_step_actions";
import { useI18n } from "./i18n";
import { ageFromDob } from "~/util/date";

function formatPhoneDisplay(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

const INPUT_CLASS =
  "rounded-lg border border-brand-subtle px-3 py-2.5 text-sm text-typography-primary transition-colors outline-none placeholder:text-typography-secondary/50 focus:border-brand-primary focus:ring-2 focus:ring-brand-focus";

function FieldInput(props: {
  label: string;
  value: string;
  onInput: (value: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  error?: string | null;
  inputMode?: string;
  maxLength?: number;
}) {
  return (
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-typography-primary">
        {props.label}
        <Show when={props.required}>
          <span class="text-red-500"> *</span>
        </Show>
      </label>
      <input
        type={props.type ?? "text"}
        inputMode={props.inputMode as "text" | "tel" | "email" | undefined}
        class={INPUT_CLASS}
        value={props.value}
        onInput={(e) => props.onInput(e.currentTarget.value)}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
      />
      <Show when={props.error}>
        <p class="text-xs text-red-600">{props.error}</p>
      </Show>
    </div>
  );
}

// "Other" needs a description when the account's intake policy says so.
function referralDetailRequired(question: ReferralQuestion, selection: string): boolean {
  return selection === question.otherValue && question.detailRequired;
}

function ReferralField(props: {
  question: ReferralQuestion;
  formData: FormData;
  onUpdate: (partial: Partial<FormData>) => void;
  locale: string;
  error?: string;
  detailError?: string;
}) {
  const t = useI18n();
  const selectId = createUniqueId();
  const selection = () => props.formData.referredFromSelection;
  const detailLabel = () => {
    if (props.question.whoValues.includes(selection())) return t("referral_detail_who");
    if (selection() === props.question.otherValue) return t("referral_detail_other");
    return t("referral_detail_optional");
  };

  return (
    <div class="flex flex-col gap-2">
      <div class="flex flex-col gap-1">
        <label for={selectId} class="text-sm font-medium text-typography-primary">
          {t("referral_question")}
          <Show when={props.question.required}>
            <span class="text-red-500"> *</span>
          </Show>
        </label>
        <select
          id={selectId}
          class={INPUT_CLASS}
          value={selection()}
          onChange={(e) => props.onUpdate({ referredFromSelection: e.currentTarget.value })}
        >
          <option value="">{t("referral_prompt")}</option>
          <For each={props.question.options}>
            {(option) => (
              <option value={option.value}>
                {option.labels[props.locale] ?? option.labels.en}
              </option>
            )}
          </For>
        </select>
        <Show when={props.error}>
          <p class="text-xs text-red-600">{props.error}</p>
        </Show>
      </div>

      <Show when={selection()}>
        <FieldInput
          label={detailLabel()}
          value={props.formData.referredFromDetail}
          onInput={(v) => props.onUpdate({ referredFromDetail: v })}
          required={referralDetailRequired(props.question, selection())}
          error={props.detailError}
        />
      </Show>
    </div>
  );
}

export function PatientInfoStep(props: {
  formData: FormData;
  onUpdate: (partial: Partial<FormData>) => void;
  onNext: () => void;
  onBack: () => void;
  isExistingPatient: boolean;
  phoneVerificationEnabled: boolean;
  locationId: string;
  locale: string;
  /** Asked of new patients only. */
  referralQuestion?: ReferralQuestion;
}) {
  const t = useI18n();
  type PatientErrors = {
    firstName?: string;
    lastName?: string;
    dob?: string;
    phone?: string;
    referral?: string;
    referralDetail?: string;
  };

  const [errors, setErrors] = createSignal<PatientErrors>({});
  const [sendingPhones, setSendingPhones] = createSignal<string[]>([]);
  const [verifying, setVerifying] = createSignal(false);
  const [verifyError, setVerifyError] = createSignal<string | null>(null);

  // The server keys a sent code and a verified state by number, so each holds for the number it
  // was done for and no other: editing the field takes the patient back to Send verification code.
  const codeSent = () => props.formData.codeSentPhones.includes(props.formData.phone);
  const phoneVerified = () => props.formData.verifiedPhones.includes(props.formData.phone);
  // Held per number, like the lists above: a send in flight for this number disables Send so a
  // second click cannot text a second code, while another number can still be sent one.
  const sendingCode = () => sendingPhones().includes(props.formData.phone);

  const phoneDisplay = () => formatPhoneDisplay(props.formData.phone);

  const handlePhoneInput = (display: string) => {
    // Store only digits
    const digits = display.replace(/\D/g, "").slice(0, 10);
    props.onUpdate({ phone: digits });
  };

  const validate = (): boolean => {
    const errs: PatientErrors = {};
    if (!props.formData.firstName.trim()) errs.firstName = t("required");
    if (!props.formData.lastName.trim()) errs.lastName = t("required");
    if (!props.formData.dob) errs.dob = t("required");
    if (!props.formData.phone.trim()) errs.phone = t("required");
    else if (props.formData.phone.replace(/\D/g, "").length < 10) errs.phone = t("invalid_phone");

    const question = props.referralQuestion;
    if (question) {
      const selection = props.formData.referredFromSelection;
      if (question.required && !selection) errs.referral = t("required");
      else if (
        referralDetailRequired(question, selection) &&
        !props.formData.referredFromDetail.trim()
      ) {
        errs.referralDetail = t("required");
      }
    }

    // Validate minimum age (5 years)
    if (props.formData.dob) {
      const age = ageFromDob(props.formData.dob);
      if (age !== null && age < 5) errs.dob = t("min_age");
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSendCode = async () => {
    // captured before the await: the field may hold a different number by the time the send lands
    const phone = props.formData.phone;
    if (!phone.trim()) return;
    setSendingPhones((prev) => [...prev, phone]);
    try {
      const sent = await sendVerificationCode(
        props.locationId,
        phone,
        props.formData.phoneVerificationId,
        props.locale,
      );
      if (sent) {
        props.onUpdate({ codeSentPhones: [...props.formData.codeSentPhones, phone] });
        setVerifyError(null);
      }
    } finally {
      setSendingPhones((prev) => prev.filter((p) => p !== phone));
    }
  };

  const handleVerify = async () => {
    if (!props.formData.phoneVerificationCode.trim()) return;
    const phone = props.formData.phone;
    setVerifying(true);
    setVerifyError(null);

    try {
      const verified = await verifyPhone(
        props.locationId,
        phone,
        props.formData.phoneVerificationId,
        props.formData.phoneVerificationCode,
      );

      if (verified) {
        props.onUpdate({ verifiedPhones: [...props.formData.verifiedPhones, phone] });
      } else {
        setVerifyError(t("invalid_code"));
      }
    } catch {
      setVerifyError(t("network_error"));
    } finally {
      setVerifying(false);
    }
  };

  const handleContinue = () => {
    if (!validate()) return;
    if (props.phoneVerificationEnabled && !phoneVerified()) {
      if (!codeSent()) {
        handleSendCode();
        return;
      }
      return;
    }
    props.onNext();
  };

  const needsVerification = () =>
    props.phoneVerificationEnabled && !phoneVerified() && props.formData.phone.trim();

  return (
    <div class="flex flex-col gap-4">
      <h2 class="text-xl font-semibold text-typography-primary">{t("your_information")}</h2>

      <div class="grid grid-cols-2 gap-3">
        <FieldInput
          label={t("first_name")}
          value={props.formData.firstName}
          onInput={(v) => props.onUpdate({ firstName: v })}
          required
          error={errors().firstName}
        />
        <FieldInput
          label={t("last_name")}
          value={props.formData.lastName}
          onInput={(v) => props.onUpdate({ lastName: v })}
          required
          error={errors().lastName}
        />
      </div>

      <Show when={!props.isExistingPatient}>
        <FieldInput
          label={t("preferred_name")}
          value={props.formData.preferredName}
          onInput={(v) => props.onUpdate({ preferredName: v })}
          placeholder={t("optional")}
        />
      </Show>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-typography-primary">
          {t("date_of_birth")} <span class="text-red-500">*</span>
        </label>
        <input
          type="date"
          class="rounded-lg border border-brand-subtle px-3 py-2.5 text-sm text-typography-primary transition-colors outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-focus"
          value={props.formData.dob}
          onInput={(e) => props.onUpdate({ dob: e.currentTarget.value })}
        />
        <Show when={errors().dob}>
          <p class="text-xs text-red-600">{errors().dob}</p>
        </Show>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-typography-primary">
          {t("phone_number")} <span class="text-red-500">*</span>
        </label>
        <input
          type="tel"
          inputMode="tel"
          class={INPUT_CLASS}
          value={phoneDisplay()}
          onInput={(e) => handlePhoneInput(e.currentTarget.value)}
          placeholder="(555) 555-5555"
          maxLength={14}
        />
        <Show when={errors().phone}>
          <p class="text-xs text-red-600">{errors().phone}</p>
        </Show>
      </div>

      {/* Phone verification. Gated on the number alone, not on needsVerification(): a
          verified number still needs this block for the green "Phone verified" banner. */}
      <Show when={props.phoneVerificationEnabled && props.formData.phone.trim()}>
        <Show
          when={codeSent()}
          fallback={
            <div class="rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
              {t("verification_disclaimer")}
            </div>
          }
        >
          <Show
            when={!phoneVerified()}
            fallback={
              <div
                role="status"
                class="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700"
              >
                <svg
                  class="size-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {t("phone_verified")}
              </div>
            }
          >
            <div class="flex flex-col gap-2">
              <div class="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  class="flex-1 rounded-lg border border-brand-subtle px-3 py-2.5 text-center text-lg tracking-widest text-typography-primary transition-colors outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-focus"
                  value={props.formData.phoneVerificationCode}
                  onInput={(e) => props.onUpdate({ phoneVerificationCode: e.currentTarget.value })}
                  placeholder="000000"
                  maxLength={6}
                />
                <button
                  type="button"
                  class="shrink-0 rounded-full px-4 py-2.5 text-sm font-medium text-white transition-all brand-button-bg hover:brand-button-bg-active disabled:opacity-50"
                  onClick={handleVerify}
                  disabled={!props.formData.phoneVerificationCode.trim() || verifying()}
                >
                  {verifying() ? t("verifying") : t("verify")}
                </button>
              </div>
              <Show when={verifyError()}>
                <p class="text-xs text-red-600">{verifyError()}</p>
              </Show>
              <button
                type="button"
                class="self-start text-xs text-brand-primary hover:underline"
                onClick={handleSendCode}
                disabled={sendingCode()}
              >
                {sendingCode() ? t("sending") : t("resend_code")}
              </button>
            </div>
          </Show>
        </Show>
      </Show>

      <Show when={!props.isExistingPatient}>
        <FieldInput
          label={t("email")}
          value={props.formData.email}
          onInput={(v) => props.onUpdate({ email: v })}
          type="email"
          placeholder={t("optional")}
        />
      </Show>

      <Show when={props.referralQuestion}>
        {(question) => (
          <ReferralField
            question={question()}
            formData={props.formData}
            onUpdate={props.onUpdate}
            locale={props.locale}
            error={errors().referral}
            detailError={errors().referralDetail}
          />
        )}
      </Show>

      <StepActions
        onBack={props.onBack}
        onNext={handleContinue}
        nextDisabled={!!needsVerification() && (sendingCode() || (codeSent() && !phoneVerified()))}
        nextLabel={
          needsVerification() && !codeSent()
            ? sendingCode()
              ? t("sending_code")
              : t("send_verification")
            : undefined
        }
      />
    </div>
  );
}
